import { useEffect, useRef, useState } from 'react';
import { trackFunnelEvent } from '../../lib/funnel-tracking';

interface Planet { sign: string; degree: number }

const ZODIAC = ['♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓'];
const ZODIAC_NAMES = ['Áries','Touro','Gêmeos','Câncer','Leão','Virgem','Libra','Escorpião','Sagitário','Capricórnio','Aquário','Peixes'];

const PT_NAME: Record<string, string> = {
  sun:'Sol', moon:'Lua', mercury:'Mercúrio', venus:'Vênus', mars:'Marte',
  jupiter:'Júpiter', saturn:'Saturno', uranus:'Urano', neptune:'Netuno', pluto:'Plutão',
  ascendant:'ASC',
};
const GLYPH: Record<string, string> = {
  sun:'☉', moon:'☽', mercury:'☿', venus:'♀', mars:'♂',
  jupiter:'♃', saturn:'♄', uranus:'♅', neptune:'♆', pluto:'♇', ascendant:'AC',
};

function signToBaseAngle(sign: string): number {
  const idx = ZODIAC_NAMES.indexOf(sign);
  if (idx === -1) return 0;
  return idx * 30;
}

function polarToXY(cx: number, cy: number, r: number, angleDeg: number) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

interface TooltipState { text: string; x: number; y: number }

interface Props {
  planets:       Record<string, Planet>;
  ascendantSign?: string | null;
  compact?:       boolean;
}

export function BirthChartVisual({ planets, ascendantSign, compact = false }: Props) {
  const [tracked, setTracked] = useState(false);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!tracked) { trackFunnelEvent('viewed_chart_visual'); setTracked(true); }
  }, [tracked]);

  const SIZE = compact ? 220 : 300;
  const CX = SIZE / 2;
  const CY = SIZE / 2;
  const R_OUTER = SIZE * 0.44;
  const R_INNER = SIZE * 0.30;
  const R_PLANET = SIZE * 0.20;

  // Map each planet to its zodiac longitude (0–359°)
  const planetPoints = Object.entries(planets).map(([key, val]) => {
    const angle = signToBaseAngle(val.sign) + val.degree;
    const pos   = polarToXY(CX, CY, R_PLANET, angle);
    return { key, angle, pos, sign: val.sign, degree: val.degree };
  });

  // Ascendant line
  const ascAngle = ascendantSign ? signToBaseAngle(ascendantSign) : null;

  return (
    <div>
      {/* SVG wheel */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem', position: 'relative' }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          width={SIZE}
          height={SIZE}
          style={{ display: 'block', overflow: 'visible' }}
          aria-label="Mapa natal, roda zodiacal"
          onMouseLeave={() => setTooltip(null)}
        >
          {/* Outer ring */}
          <circle cx={CX} cy={CY} r={R_OUTER} fill="none" stroke="rgba(31,78,95,0.35)" strokeWidth="1" />
          {/* Inner ring */}
          <circle cx={CX} cy={CY} r={R_INNER} fill="none" stroke="rgba(31,78,95,0.2)"  strokeWidth="0.5" />
          {/* Planet ring guide */}
          <circle cx={CX} cy={CY} r={R_PLANET} fill="none" stroke="rgba(31,78,95,0.12)" strokeWidth="0.5" strokeDasharray="2,4" />

          {/* 12 zodiac divisions */}
          {ZODIAC.map((glyph, i) => {
            const divAngle = i * 30;
            const p1 = polarToXY(CX, CY, R_INNER,  divAngle);
            const p2 = polarToXY(CX, CY, R_OUTER,  divAngle);
            const pt = polarToXY(CX, CY, (R_INNER + R_OUTER) / 2, divAngle + 15);
            return (
              <g key={i}>
                <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="rgba(31,78,95,0.22)" strokeWidth="0.5" />
                <text x={pt.x} y={pt.y} textAnchor="middle" dominantBaseline="central"
                  fontSize={compact ? 9 : 11} fill="rgba(31,78,95,0.7)" style={{ fontFamily: 'serif' }}>
                  {glyph}
                </text>
              </g>
            );
          })}

          {/* Ascendant line */}
          {ascAngle !== null && (() => {
            const p1 = polarToXY(CX, CY, R_INNER - 4, ascAngle);
            const p2 = polarToXY(CX, CY, R_OUTER + 4, ascAngle);
            return <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="var(--petrol)" strokeWidth="1.5" strokeDasharray="3,2" opacity="0.6" />;
          })()}

          {/* Planet glyphs */}
          {planetPoints.map(({ key, pos }) => (
            <g key={key}>
              <circle cx={pos.x} cy={pos.y} r={compact ? 8 : 10}
                fill="var(--bg-elevated)" stroke="var(--petrol)" strokeWidth="0.8" />
              <text x={pos.x} y={pos.y} textAnchor="middle" dominantBaseline="central"
                fontSize={compact ? 8 : 9} fill="var(--petrol)"
                style={{ cursor: 'pointer', fontWeight: 600 }}
                onMouseEnter={(e) => {
                  setTooltip({
                    text: `${PT_NAME[key] ?? key} em ${planets[key]?.sign ?? ''} (${Math.round(planets[key]?.degree ?? 0)}°)`,
                    x: e.clientX, y: e.clientY,
                  });
                }}
              >
                {GLYPH[key] ?? key[0].toUpperCase()}
              </text>
            </g>
          ))}

          {/* Centre dot */}
          <circle cx={CX} cy={CY} r="3" fill="var(--petrol)" opacity="0.5" />
        </svg>

        {/* Tooltip */}
        {tooltip && (
          <div style={{
            position:     'fixed',
            left:         tooltip.x + 12,
            top:          tooltip.y - 8,
            background:   'var(--bg-elevated)',
            border:       '1px solid var(--line-soft)',
            borderRadius: 'var(--radius-sm)',
            padding:      '0.3rem 0.6rem',
            fontSize:     'var(--text-caption)',
            color:        'var(--bg)',
            pointerEvents: 'none',
            zIndex:       9999,
            whiteSpace:   'nowrap',
            boxShadow:    '0 2px 8px rgba(0,0,0,0.15)',
          }}>
            {tooltip.text}
          </div>
        )}
      </div>

      {/* Planet grid fallback / supplement */}
      <div style={{
        display:             'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
        gap:                 '0.5rem',
      }}>
        {Object.entries(planets).map(([key, val]) => (
          <div key={key} style={{
            background:   'var(--bg-elevated)',
            border:       '1px solid var(--line-soft)',
            borderRadius: 'var(--radius-sm)',
            padding:      '0.5rem 0.75rem',
          }}>
            <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 700 }}>
              {GLYPH[key] ?? ''} {PT_NAME[key] ?? key}
            </p>
            <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
              {val.sign} · {Math.round(val.degree)}°
            </p>
          </div>
        ))}
        {ascendantSign && (
          <div style={{
            background:   'var(--bg-elevated)',
            border:       '1px solid var(--line-soft)',
            borderRadius: 'var(--radius-sm)',
            padding:      '0.5rem 0.75rem',
          }}>
            <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 700 }}>AC Ascendente</p>
            <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>{ascendantSign}</p>
          </div>
        )}
      </div>

      <p style={{ textAlign: 'center', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '0.75rem' }}>
        Passe o mouse sobre os planetas para detalhes
      </p>
    </div>
  );
}
